import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import TechnologyCompanyApi from 'api/b2b/TechnologyCompanyApi'
import OrbitSession from 'hooks/useCookies'
import { debounce } from 'lodash'

import TechnologyListTable from './TechnologyList'

export type TechnologyCompany = {
  id: string
  companyName: string
  commissionType?: string
  currency?: string
  value?: string
  isActive?: boolean
  emailId?: string
  createdBy: string
  createdOn: string
  updatedBy: string
  updatedOn: string
}
type FormValues = {
  companyName: string | null
}

function SearchTechnologyCompany() {
  const navigate = useNavigate()
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: { companyName: null }
  })

  // State management
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedCompany, setSelectedCompany] =
    useState<TechnologyCompany | null>(null)
  const [displayData, setDisplayData] = useState<TechnologyCompany[]>([])

  // Watch the form field for changes
  const companyNameValue = watch('companyName')

  // Debounced function to update search term
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term) => {
        setDebouncedSearchTerm(term)
      }, 500),
    []
  )

  // Effect to trigger debounced search when input changes
  useEffect(() => {
    if (companyNameValue && typeof companyNameValue === 'string') {
      debouncedSetSearchTerm(companyNameValue.trim())
    } else {
      debouncedSetSearchTerm('')
    }

    return () => {
      debouncedSetSearchTerm.cancel()
    }
  }, [companyNameValue, debouncedSetSearchTerm])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeTenant = (d: any): TechnologyCompany => ({
    id: d.id,
    companyName: d.technology_company_name,
    commissionType: d.commission_type,
    currency: d.currency,
    value: d.value,
    isActive: d.is_active,
    emailId: d.email_id,
    createdBy: d.created_by,
    createdOn: d.created_at,
    updatedBy: d.updated_by,
    updatedOn: d.updated_at
  })
  // Query for searching companies with debounced search term
  const { data: searchResults = [], isLoading: isSearching } = useQuery(
    ['searchTechnologyCompanies', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const response = await TechnologyCompanyApi.getTechnologyCompanyDetails(
        token,
        debouncedSearchTerm
      )
      const data = response.data
      return Array.isArray(data)
        ? data.map(normalizeTenant)
        : [normalizeTenant(data)]
    },
    {
      enabled: !!debouncedSearchTerm && debouncedSearchTerm.length > 2,
      onError: () => {
        toast.error('Error searching technology companies')
      }
    }
  )

  // Handle form submission (Search button)
  const onSubmit = () => {
    if (!selectedCompany) {
      toast.error('Please select a company first')
      return
    }

    // Now selectedCompany already has full tenant info
    setDisplayData([selectedCompany])
  }

  // Handle reset
  const onReset = () => {
    reset()
    setSelectedCompany(null)
    setDisplayData([])
    setDebouncedSearchTerm('')
  }

  // Handle company selection from autocomplete
  const handleCompanySelect = (event, newValue) => {
    setSelectedCompany(newValue)
  }

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end'>
        <Button onClick={() => navigate('add')}>Add</Button>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px', fontSize: '14px' }}>
            Technology Company*
          </Typography>
          <Controller
            name='companyName'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                value={selectedCompany}
                onChange={handleCompanySelect}
                inputValue={typeof value === 'string' ? value : ''}
                onInputChange={(event, newInputValue) => {
                  onChange(newInputValue)
                }}
                options={searchResults || []}
                getOptionLabel={(option) => option?.companyName || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                loading={isSearching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    size='small'
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      ...params.InputProps
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (!option) return null
                  return (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='body2' fontWeight='medium'>
                          {option.companyName || 'No name'}
                        </Typography>
                        {option.id && (
                          <Typography variant='caption' color='text.secondary'>
                            {option.id}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                }}
                sx={{ minWidth: 300 }}
              />
            )}
          />
        </Box>

        <Box mt='10px'>
          <Button variant='contained' type='submit' disabled={!selectedCompany}>
            Search
          </Button>
          <Button
            variant='contained'
            type='button'
            onClick={onReset}
            sx={{ ml: 1 }}
          >
            Reset
          </Button>
        </Box>
      </form>

      <Divider sx={{ my: 2 }} />

      <Box>
        <TechnologyListTable
          data={displayData}
          setDisplayData={setDisplayData}
          // isLoading={false}
          // hasSearched={hasSearched}
        />
      </Box>
    </Box>
  )
}

export default SearchTechnologyCompany
